import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Table, Button, Group, Text, Paper, Container,
  Grid, Card, Image, Alert, Stack,
  Divider, ScrollArea, AspectRatio, Skeleton
} from '@mantine/core';
import {
  IconTruckDelivery, IconArrowLeft,
  IconAlertCircle, IconFileInvoice, IconRulerMeasure, IconWeight,
  IconCoin, IconTruck, IconPackage
} from '@tabler/icons-react';
import { PackageService } from '../types';
import { CombinedPackageService } from '../types';
import PageTitle from '../components/common/PageTitle';
import { notifications } from '@mantine/notifications';

// Define the package type
interface Package {
  id: number;
  description: string;
  weight: string;
  length: string;
  width: string;
  height: string;
  invoice_image: string | null;
  image: string;
  note: string | null;
  custom_value: string;
  quantity: number;
  shipping_cost: string;
  isDraft: boolean;
  created_at: string;
  updated_at: string;
  user_id: string;
  combinedPackageId: string | null;
  item: Array<{
    id: number;
    name: string;
    description: string;
    weight: string;
    dimensions: string;
    category: string;
    price: string;
    currency: string;
    quantity: number;
    packageId: number;
  }>;
  shipment: any | null;
}

// =================== Loading Skeleton Components ===================
const HeaderSkeleton = () => (
  <Paper p="md" withBorder mb="lg">
    <Group mb="md">
      <Skeleton height={24} circle />
      <Skeleton height={36} width={200} radius="md" />
    </Group>
    <Group gap="xs">
      <Skeleton height={24} width={100} />
      <Skeleton height={24} width={20} />
      <Skeleton height={24} width={100} />
      <Skeleton height={24} width={20} />
      <Skeleton height={24} width={100} />
    </Group>
  </Paper>
);

const DetailsSkeleton = () => (
  <Card withBorder radius="md" p="md">
    <Group mb="lg" gap="xs">
      <Skeleton height={20} circle />
      <Skeleton height={24} width={150} />
    </Group>
    <Stack gap="xs">
      {Array(5).fill(0).map((_, i) => (
        <div key={i}>
          <Group justify="space-between" wrap="nowrap">
            <Group gap="xs">
              <Skeleton height={18} circle />
              <Skeleton height={20} width={100} />
            </Group>
            <Skeleton height={20} width={80} />
          </Group>
          {i < 4 && <Divider my="xs" />}
        </div>
      ))}
    </Stack>
  </Card>
);

const ItemsSkeleton = () => (
  <Paper withBorder radius="md" p="md" mt="lg">
    <Group mb="lg" gap="xs">
      <Skeleton height={20} circle />
      <Skeleton height={24} width={150} />
    </Group>
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th><Skeleton height={20} width={100} /></Table.Th>
          <Table.Th><Skeleton height={20} width={80} /></Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {Array(3).fill(0).map((_, i) => (
          <Table.Tr key={i}>
            <Table.Td><Skeleton height={20} width="90%" /></Table.Td>
            <Table.Td><Skeleton height={20} width={40} /></Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  </Paper>
);

const ImageSkeleton = () => (
  <Card padding="lg" withBorder radius="md" mb="md">
    <Card.Section>
      <AspectRatio ratio={16 / 9}>
        <Skeleton height="100%" width="100%" />
      </AspectRatio>
    </Card.Section>
  </Card>
);

const ActionsSkeleton = () => (
  <Paper withBorder radius="md" mt="xl">
    <Skeleton height={50} radius="md" />
  </Paper>
);



interface PackageImageProps {
  image: string;
  packageId: number;
  onImageLoad: () => void;
  imageLoading: boolean;
}

const PackageImage = ({ image, packageId, onImageLoad, imageLoading }: PackageImageProps) => {
  return (
    <Card padding="lg" withBorder radius="md" mb="md">
      <Card.Section>
        <AspectRatio ratio={16 / 9}>
          {imageLoading && <Skeleton height="100%" width="100%" />}
          <Image
            src={image}
            alt={`Package ${packageId}`}
            fit="cover"
            onLoad={onImageLoad}
            style={{ visibility: imageLoading ? 'hidden' : 'visible' }}
            loading="lazy"
          />
        </AspectRatio>
      </Card.Section>
    </Card>
  );
};

interface PackageDetailsCardProps {
  packageData: Package;
  formatDimensions: (length: string, width: string, height: string) => string;
  invoiceFile: File | null;
  setInvoiceFile: (file: File | null) => void;
  handleUploadInvoice: () => void;
  isUploading: boolean;
  onDeclaredValueChange?: (newValue: string) => void;
}

const PackageDetailsCard = ({
  packageData,
  formatDimensions,
  onDeclaredValueChange }: PackageDetailsCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(packageData.custom_value);

  const handleEditClick = () => {
    setIsEditing(true);
    setEditValue(packageData.custom_value);
  };

  const handleSave = () => {
    onDeclaredValueChange?.(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(packageData.custom_value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };
  return (
    <Card withBorder radius="md" p="md">
      <PageTitle
        title="Package Details"
        size="sm"
        icon={<IconPackage size={20} stroke={1.5} />}
      />
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <IconWeight size={18} color="gray" />
            <Text fw={500}>Weight</Text>
          </Group>
          <Text>{packageData.weight} kg</Text>
        </Group>

        <Divider />

        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <IconRulerMeasure size={18} color="gray" />
            <Text fw={500}>Dimensions</Text>
          </Group>
          <Text>{formatDimensions(packageData.length, packageData.width, packageData.height)}</Text>
        </Group>

        {/* <Divider /> */}

        {/* <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <IconBarcode size={18} color="gray" />
            <Text fw={500}>Tracking Number</Text>
          </Group>
          <Text>N/A</Text>
        </Group> */}

        <Divider />

        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <IconCoin size={18} color="gray" />
            <Text fw={500}>Declared Value</Text>
          </Group>
          {isEditing ? (
            <Group gap="xs">
              <Text>$</Text>
              <input
                type="number"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  width: '80px',
                  padding: '4px 8px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                }}
                autoFocus
              />
              <Button size="xs" variant="subtle" color="gray" onClick={handleCancel}>
                Cancel
              </Button>
              <Button size="xs" onClick={handleSave}>
                Save
              </Button>
            </Group>
          ) : (
            <Group gap="xs">
              <Text>${packageData.custom_value}</Text>
              <Button
                variant="subtle"
                size="xs"
                onClick={handleEditClick}
                style={{ padding: '0 4px' }}
              >
                Edit
              </Button>
            </Group>
          )}
        </Group>

        <Divider />

        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <IconTruck size={18} color="gray" />
            <Text fw={500}>Shipping Cost</Text>
          </Group>
          <Text>${packageData.shipping_cost || 'Pending calculation'}</Text>
        </Group>

        {/* {packageData.invoice_image && (
          <>
            <Divider />
            <Group justify="space-between" wrap="nowrap">
              <Group gap="xs">
                <IconFileInvoice size={18} color="gray" />
                <Text fw={500}>Invoice</Text>
              </Group>
              <Anchor href={packageData.invoice_image} target="_blank" rel="noopener noreferrer">
                View Invoice
              </Anchor>
            </Group>
          </>
        )}

        {!packageData.invoice_image && (
          <>
            <Divider />
            <Group gap="xs">
              <IconFileInvoice size={18} color="gray" />
              <Text fw={500}>Invoice</Text>
            </Group>
            <Group align="flex-end" gap="xs" mt="xs">
              <FileInput
                placeholder="Upload invoice"
                accept="application/pdf,image/jpeg,image/png"
                value={invoiceFile}
                onChange={setInvoiceFile}
                style={{ flex: 1 }}
                disabled={isUploading}
              />
              <Button
                leftSection={<IconUpload size={16} />}
                onClick={handleUploadInvoice}
                disabled={!invoiceFile || isUploading}
                loading={isUploading}
                size="sm"
              >
                Upload
              </Button>
            </Group>
          </>
        )} */}
      </Stack>
    </Card>
  );
};

interface PackageItemsProps {
  items: Array<any>;
  onItemValueChange: (index: number, newValue: string) => void;
}

const PackageItems = ({ items, onItemValueChange }: PackageItemsProps) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");

  const handleEditClick = (index: number, currentValue: string) => {
    setEditingIndex(index);
    setEditValue(currentValue);
  };

  const handleSave = (index: number) => {
    onItemValueChange(index, editValue);
    setEditingIndex(null);
  };

  const handleCancel = () => {
    setEditingIndex(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter') {
      handleSave(index);
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <Paper withBorder radius="md" p="md" mt="lg">
      <PageTitle
        title="Declared Items"
        size="sm"
        icon={<IconFileInvoice size={20} stroke={1.5} />}
      />
      <ScrollArea>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Item</Table.Th>
              <Table.Th>Quantity</Table.Th>
              <Table.Th>Value</Table.Th>
              <Table.Th>Total</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items && items.length > 0 ? (
              items.map((item, index) => (
                <Table.Tr key={index}>
                  <Table.Td>{item.name}</Table.Td>
                  <Table.Td>{item.quantity}</Table.Td>
                  <Table.Td>
                    {editingIndex === index ? (
                      <Group gap="xs">
                        <Text>$</Text>
                        <input
                          type="number"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          style={{
                            width: '80px',
                            padding: '4px 8px',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                          }}
                          autoFocus
                        />
                        <Button size="xs" variant="subtle" color="gray" onClick={handleCancel}>
                          Cancel
                        </Button>
                        <Button size="xs" onClick={() => handleSave(index)}>
                          Save
                        </Button>
                      </Group>
                    ) : (
                      <Group gap="xs">
                        <Text>${item.price || '0'}</Text>
                        <Button
                          variant="subtle"
                          size="xs"
                          onClick={() => handleEditClick(index, item.price || '0')}
                          style={{ padding: '0 4px' }}
                        >
                          Edit
                        </Button>
                      </Group>
                    )}
                  </Table.Td>
                  <Table.Td>${(Number(item.price || 0) * Number(item.quantity)).toFixed(2)}</Table.Td>
                </Table.Tr>
              ))
            ) : (
              <Table.Tr>
                <Table.Td colSpan={4} style={{ textAlign: 'center' }}>
                  No items declared
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Paper>
  );
};

interface PackageActionsProps {
  handleShipPackage: () => void;
}

const PackageActions = ({ handleShipPackage }: PackageActionsProps) => {
  return (
    <Button
      leftSection={<IconTruckDelivery size={20} />}
      size="lg"
      variant="filled"
      color="blue"
      onClick={handleShipPackage}
      fullWidth
      mt="xl"
    >
      Ship This Package
    </Button>
  );
};

const PackageDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [packageData, setPackageData] = useState<Package | null>(null);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Read package type from URL parameters
  const packageFilterType = searchParams.get('type') || 'received';

  // Define formatDimensions before it's used in the useEffect
  const formatDimensions = useCallback((length: string, width: string, height: string) => {
    return `${length} × ${width} × ${height} cm`;
  }, []);

  // Add a function to map combined package to Package interface
  const mapCombinedPackageToPackage = useCallback((combinedPackage: any): Package => {
    // Map the main combined package properties
    const mappedPackage: Package = {
      id: combinedPackage.id,
      description: combinedPackage.description || 'Combined Package',
      weight: combinedPackage.weight,
      length: combinedPackage.length,
      width: combinedPackage.width,
      height: combinedPackage.height,
      image: combinedPackage.image || (combinedPackage.package && combinedPackage.package.length > 0 ? combinedPackage.package[0].image : ''),
      custom_value: combinedPackage.custom_value,
      quantity: combinedPackage.quantity,
      shipping_cost: combinedPackage.shipping_cost,
      invoice_image: null, // Combined packages don't have invoice images
      note: null,
      isDraft: false, // Combined packages are never drafts
      created_at: new Date().toISOString(), // Use current date as fallback
      updated_at: new Date().toISOString(),
      user_id: combinedPackage.user_id,
      combinedPackageId: null,
      // Flatten items from all packages into one array
      item: [],
      shipment: null
    };

    // Process each package in the combined package and collect all items
    if (combinedPackage.package && Array.isArray(combinedPackage.package)) {
      // First, add each package as an item
      // mappedPackage.item = combinedPackage.package.map((pkg: any) => ({
      //   id: pkg.id,
      //   name: pkg.description || `Package #${pkg.id}`,
      //   description: pkg.description || `Package #${pkg.id}`,
      //   weight: pkg.weight,
      //   dimensions: `${pkg.length} × ${pkg.width} × ${pkg.height} cm`,
      //   category: 'Package',
      //   price: pkg.custom_value,
      //   currency: 'USD',
      //   quantity: pkg.quantity || 1,
      //   packageId: pkg.id
      // }));

      // Then, extract all items from all packages and add them too
      combinedPackage.package.forEach((pkg: any) => {
        if (pkg.item && Array.isArray(pkg.item)) {
          mappedPackage.item = [
            ...mappedPackage.item,
            ...pkg.item.map((item: any) => ({
              ...item,
              packageId: pkg.id
            }))
          ];
        }
      });

      // Calculate total weight and value from all packages if not provided
      if (mappedPackage.weight === "0") {
        const totalWeight = combinedPackage.package.reduce(
          (sum: number, pkg: any) => sum + parseFloat(pkg.weight || '0'),
          0
        );
        mappedPackage.weight = totalWeight.toString();
      }

      if (mappedPackage.custom_value === "0") {
        const totalValue = combinedPackage.package.reduce(
          (sum: number, pkg: any) => sum + parseFloat(pkg.custom_value || '0'),
          0
        );
        mappedPackage.custom_value = totalValue.toString();
      }
    }

    return mappedPackage;
  }, []);

  // Fetch package data function
  const fetchPackageData = useCallback(async () => {
    if (!id) {
      setError('Package ID is missing');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setDetailsLoading(true);
    setItemsLoading(true);
    setError(null);

    try {

      // Use appropriate service based on package type
      if (packageFilterType === 'consolidated') {
        // For consolidated packages, use CombinedPackageService
        const res = await CombinedPackageService.getPackageCombine1(parseInt(id, 10));

        if (!res) {
          throw new Error('Consolidated package not found');
        }

        // Map the combined package response to the Package interface
        const mappedPackage = mapCombinedPackageToPackage(res);
        setPackageData(mappedPackage);
      } else {
        // For received packages (default), use PackageService
        const res: any = await PackageService.getPackage1(parseInt(id, 10));
        if (!res) {
          throw new Error('Package not found');
        }
        setPackageData(res);
      }
    } catch (err) {
      setError('Failed to load package details. Please try again.');
    } finally {
      setIsLoading(false);
      // Stagger loading states to improve perceived performance
      setTimeout(() => setDetailsLoading(false), 100);
      setTimeout(() => setItemsLoading(false), 200);
    }
  }, [id, packageFilterType, mapCombinedPackageToPackage]);

  useEffect(() => {
    fetchPackageData();
  }, [fetchPackageData]); // Now depends on fetchPackageData which includes packageFilterType

  const handleShipPackage = useCallback(() => {
    // Navigate to shipping page with package ID and type`/shipments/new?packageId=${id}`
    navigate(`/shipments/new?packageId=${id}&type=${packageFilterType}`);
  }, [navigate, id, packageFilterType]);

  const handleUploadInvoice = useCallback(async () => {
    if (!invoiceFile || isUploading) {
      notifications.show({
        title: 'No Invoice File',
        message: 'Please select an invoice file first',
        color: 'yellow',
      });
      return;
    }

    // In a real app, this would upload the file to the backend
    try {
      setIsUploading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Update the package data with the new invoice
      if (packageData) {
        setPackageData({
          ...packageData,
          invoice_image: URL.createObjectURL(invoiceFile) // In a real app, this would be the URL from the server
        });
      }

      notifications.show({
        title: 'Success',
        message: 'Invoice uploaded successfully!',
        color: 'green',
      });
      setInvoiceFile(null);
    } catch (err) {
      notifications.show({
        title: 'Upload Failed',
        message: 'Failed to upload invoice. Please try again.',
        color: 'red',
      });
    } finally {
      setIsUploading(false);
    }
  }, [invoiceFile, packageData, isUploading]);


  const handleImageLoad = useCallback(() => {
    setImageLoading(false);
  }, []);

  // Render error state
  if (!isLoading && (error || !packageData)) {
    return (
      <Container>
        <Alert
          title="Error"
          color="red"
          icon={<IconAlertCircle />}
          mb="lg"
        >
          {error || 'Package not found'}
        </Alert>
        <Button
          leftSection={<IconArrowLeft size={16} />}
          variant="light"
          component={Link}
          to="/packages"
        >
          Back to Packages
        </Button>
      </Container>
    );
  }

  // Render loading state
  if (isLoading) {
    return (
      <Container size="xl" px="xs">
        <HeaderSkeleton />
        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ImageSkeleton />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <DetailsSkeleton />
          </Grid.Col>
        </Grid>
        <ItemsSkeleton />
        <ActionsSkeleton />
      </Container>
    );
  }

  return (
    <Container py="xl" size="lg" className="package-detail-page">
      {isLoading ? (
        <HeaderSkeleton />
      ) : error ? (
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          {error}
        </Alert>
      ) : (
        <PageTitle
          title={`Package #${id}`}
          icon={<IconPackage size={24} stroke={1.5} />}
          breadcrumbs={[
            { title: 'Dashboard', href: '/dashboard' },
            { title: 'Packages', href: '/packages' },
            { title: `Package #${id}` }
          ]}
        />
      )}

      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 6 }}>
          {(!packageData || (imageLoading && !packageData.image)) ? <ImageSkeleton /> : (
            <PackageImage
              image={packageData.image}
              packageId={packageData.id}
              onImageLoad={handleImageLoad}
              imageLoading={imageLoading}
            />
          )}
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          {detailsLoading || !packageData ? <DetailsSkeleton /> : (
            <PackageDetailsCard
              packageData={packageData}
              formatDimensions={formatDimensions}
              invoiceFile={invoiceFile}
              setInvoiceFile={setInvoiceFile}
              handleUploadInvoice={handleUploadInvoice}
              isUploading={isUploading}
              onDeclaredValueChange={(newValue) => {
                if (packageData) {
                  setPackageData({
                    ...packageData,
                    custom_value: newValue
                  });
                }
              }}
            />
          )}
        </Grid.Col>
      </Grid>
      {itemsLoading ? <ItemsSkeleton /> : (
        <PackageItems
          items={packageData?.item || []}
          onItemValueChange={async (index: number, newValue: string) => {
            if (!packageData) return;

            const updatedItems = [...packageData.item];
            updatedItems[index] = {
              ...updatedItems[index],
              price: newValue
            };

            // Convert weight to number for each item to match Item[] type
            const itemsForApi = updatedItems.map(item => ({
              ...item,
              weight: typeof item.weight === 'string' ? parseFloat(item.weight) : item.weight,
              price: typeof item.price === 'string' ? parseFloat(item.price) : item.price
            }));

            // Calculate new total declared value
            const newDeclaredValue = updatedItems.reduce((sum, item) => {
              return sum + (Number(item.price || 0) * Number(item.quantity));
            }, 0).toFixed(2);

            // Update local state
            setPackageData({
              ...packageData,
              item: updatedItems,
              custom_value: newDeclaredValue
            });

            try {
              // Call the API to update the package
              await PackageService.putPackage(packageData.id, {
                custom_value: newDeclaredValue,
                items: itemsForApi
              });
            } catch (error) {
              notifications.show({
                title: 'Error',
                message: 'Failed to update item value. Please try again.',
                color: 'red',
              });
            }
          }}
        />
      )}
      {isLoading ? <ActionsSkeleton /> : (
        <PackageActions handleShipPackage={handleShipPackage} />
      )}
    </Container>
  );
};

export default PackageDetail;